import { assignInlineVars } from "@vanilla-extract/dynamic"
import {
  createEffect,
  createMemo,
  createSignal,
  type JSX,
  onMount,
} from "solid-js"
import { containerRecipe, maxHeightVar } from "./clipped-content.css"

interface ClippedContentProps {
  maxHeight: number
  children: JSX.Element
  class?: string
}

export function ClippedContent(props: ClippedContentProps) {
  const [containerRef, setContainerRef] = createSignal<HTMLDivElement>()
  const [shouldShowGradient, setShouldShowGradient] = createSignal(false)

  function checkOverflow() {
    const container = containerRef()
    if (!container) return

    const isOverflowing = container.scrollHeight > container.clientHeight
    setShouldShowGradient(isOverflowing)
  }

  onMount(() => {
    checkOverflow()
  })

  createEffect(() => {
    const container = containerRef()
    if (!container) return

    let rafId: number | undefined
    function scheduleCheck() {
      if (rafId !== undefined) return
      rafId = requestAnimationFrame(() => {
        rafId = undefined
        checkOverflow()
      })
    }

    const observer = new ResizeObserver(scheduleCheck)
    observer.observe(container)

    const mutationObserver = new MutationObserver(scheduleCheck)
    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    })

    return () => {
      if (rafId !== undefined) cancelAnimationFrame(rafId)
      observer.disconnect()
      mutationObserver.disconnect()
    }
  })

  const className = createMemo(() =>
    [props.class, containerRecipe({ hasGradient: shouldShowGradient() })]
      .filter(Boolean)
      .join(" "),
  )

  return (
    <div
      ref={setContainerRef}
      class={className()}
      style={assignInlineVars({
        [maxHeightVar]: `${props.maxHeight}px`,
      })}
    >
      {props.children}
    </div>
  )
}
